import React, { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import UserInviteForm from '../../UserInviteForm';

import useAlarm from '../../../context/AlarmContext';
import useModal from '../../../context/ModalContext';
import useAuth from '../../../context/AuthContext';
import FormLoader from '../../FormLoader';
import SimplifiedTable from '../../SimplifiedUserTable';
import { adminAPI } from '../../../api';
import ApplicationSelect from './invite/ApplicationsSelect';
import EmailInputList from './invite/EmailInputList';
import FeedbackModal from './invite/FeedbackModal';
import ConfirmModal from './ConfirmModal';

const IndependentUserInviteForm = ({
  setOpenSideActionBoard,
  items,
  title,
  type,
  setCheckedActiveUsers,
  setCheckedAllActiveUsers,
  setUnconfirmedUsers,
  setCheckedAllUnconfirmedUsers,
  setPendingInvites,
  setCheckedAllPendingInvites,
  checkedAllUsers,
  totalUsers,
  cohorts,
  filter,
  searchQuery,
}) => {
  const [emailList, setEmailList] = useState([]);
  const [bulkEmailCount, setBulkEmailCount] = useState(0);
  const [bulkEmailList, setBulkEmailList] = useState([]);
  const [application, setApplication] = useState('ALD+ALEX');
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingMessage, setProcessingMessage] = useState('');
  const { userData } = useAuth();
  
  const { data, isLoading } = useQuery(
    'invidation_data',
    adminAPI.getInvitationData
  );

  const initialFormData = {
    access_type: '5 Free Videos',
    alex_access_type: 'Trial Access',
    expires_at: null,
    unlimited_gifting: false,
    invitation_access: 'limited',
    skip_email: items ? true : false,
    course_ids: [],
    discount_cents: null,
    opt_out_eop: false,
    expiration_type: 'length',
    alex_expiration_type: 'fixed_date',
    valid_for_days: 7,
    length_type: 'days',
    alex_length_type: 'days',
    is_alex_available: true,
    cohorts: null,
    checkedAllUsers: false,
    alex_expiration_date: false,
    invited_by_id: null,
    invited_by_email: '',
  };

  const [formData, setFormData] = useState(initialFormData);
  const [selected, setSelected] = useState(items);
  const [includedFields, setIncludedFields] = useState([
    'emails',
    'skip_email',
    'opt_out_eop',
    'cohorts'
  ]);

  const canDefaultSelfAsInviter = (u) =>
    u?.id &&
    ((u.email && /@(admiredleadership|crainc)\.com$/i.test(u.email)) ||
      u.is_employee === true ||
      (Array.isArray(u.roles) && u.roles.includes('employee')));

  useEffect(() => {
    if (items || !canDefaultSelfAsInviter(userData)) return;
    setFormData((prev) => {
      if (prev.invited_by_id != null && prev.invited_by_id !== '') return prev;
      return {
        ...prev,
        invited_by_id: userData.id,
        invited_by_email: userData.email || '',
      };
    });
  }, [items, userData]);

  const toggleIncludedField = (toggle, field) => {
    if (toggle) {
      setIncludedFields((prev) => [...prev, field]);
    } else {
      setIncludedFields((prev) => prev.filter((f) => f !== field));
    }
  };

  const handleEmailListChange = (emails, bulkCount = 0, bulkEmails = []) => {
    setEmailList(emails);
    setBulkEmailCount(bulkCount);
    setBulkEmailList(bulkEmails);
  };

  const handleChange = (name, value, parent = null) => {
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  // useEffect(() => {
  //   if (application === 'ALEX') {
  //     handleChange('access_type', 'ALEX', 'user_invite');
  //     handleChange('alex_access_type', 'Trial Access', 'user_invite');
  //   } else {
  //     handleChange('access_type', '5 Free Videos', 'user_invite');
  //   }
  // }, [application]);

  const bulkEmailLimit = parseInt(window.ENV?.BULK_EMAIL_LENGTH || process.env.REACT_APP_BULK_EMAIL_LENGTH || '1', 10);

  const getChangedFields = (current, initial) => {
    const changed = {};
    const fieldsToCheck = items && includedFields ? includedFields : Object.keys(current);
    
    fieldsToCheck.forEach((key) => {
      if (key === 'checkedAllUsers' || key === 'application') {
        return;
      }
      
      const currentValue = current[key];
      const initialValue = initial[key];
      
      if (items && includedFields && includedFields.includes(key)) {
        if (current.hasOwnProperty(key)) {
          changed[key] = currentValue;
        }
      } else {
        if (currentValue !== initialValue) {
          if (Array.isArray(currentValue) && Array.isArray(initialValue)) {
            if (JSON.stringify(currentValue) !== JSON.stringify(initialValue)) {
              changed[key] = currentValue;
            }
          } else {
            changed[key] = currentValue;
          }
        }
      }
    });

    // When Invitation Length (valid_for_days) is included in bulk edit, also send length_type so backend receives Days/Years.
    if (items && includedFields?.includes('valid_for_days') && current.hasOwnProperty('length_type')) {
      changed.length_type = current.length_type;
    }

    if (items && includedFields?.includes('invited_by_id')) {
      changed.invited_by_id = current.invited_by_id;
      changed.invited_by_email = current.invited_by_email;
    }

    return changed;
  };

  const queryClient = useQueryClient();

  const queryListId =
    type === 'Active Users'
      ? 'activeUsers'
      : type === 'Unconfirmed Users'
      ? 'unconfirmedUsers'
      : 'pendingInvites';

  const { setAlarm } = useAlarm();
  const { setContent, closeModal } = useModal();

  const { mutate, isLoading: mutationIsLoading } = useMutation(
    adminAPI.inviteUsers,
    {
      onSuccess: (data) => {
        if (data?.job_id ) {
          setProcessingMessage(data.message);
          setAlarm({ 
            type: 'success', 
            message: data.message
          });
        } else {
          setContent({
            content: <FeedbackModal results={data?.results} skipEmail={data?.skip_email} />,
          });
        }
        setOpenSideActionBoard();
        if (type === 'Active Users') {
          setCheckedActiveUsers([]);
          setCheckedAllActiveUsers(false);
        }
        if (type === 'Unconfirmed Users') {
          setUnconfirmedUsers([]);
          setCheckedAllUnconfirmedUsers(false);
        }
        if (type === 'Pending Invites') {
          setPendingInvites([]);
          setCheckedAllPendingInvites(false);
        }
        queryClient.invalidateQueries(queryListId);
        queryClient.invalidateQueries('userDetails');
        setSelected([]);
      },
      onError: (error) => {
        setAlarm({ type: 'error', message: error.message });
      },
    }
  );

  const handleSubmit = async (event) => {
    event.preventDefault();
    formData.application = application;

    if(application == 'ALEX') {
      formData.access_type = 'ALEX'
    }
    
    let totalRecords = 0;
    if (checkedAllUsers === true && totalUsers > bulkEmailLimit) {
      totalRecords = totalUsers;
    } else if (selected && selected.length > 0) {
      totalRecords = selected.length;
    } else if (emailList && emailList.length > 0) {
      totalRecords = emailList.length;
    } else if (items && items.length > 0) {
      totalRecords = items.length;
    } else if (bulkEmailCount > 0) {
      totalRecords = bulkEmailCount;
    }

    if (totalRecords === 0) {
      setAlarm({ type: 'error', message: 'No emails provided.' });
      return;
    }

    const sendDataKeys = Object.keys(getChangedFields(formData, initialFormData));
    const hasOnlySkipEmailAndOptOut = 
      sendDataKeys.length === 2 &&
      sendDataKeys.includes('skip_email') &&
      sendDataKeys.includes('opt_out_eop');
    
    if (hasOnlySkipEmailAndOptOut) {
      setAlarm({ type: 'error', message: 'No information was changed, nothing to update.' });
      return;
    }

    setContent({
      content: (
        <ConfirmModal
          title={title}
          totalRecords={totalRecords}
          onConfirm={async () => {
            try {
              if (totalUsers > bulkEmailLimit) {
                const changedFields = items 
                  ? getChangedFields(formData, initialFormData)
                  : formData;
                
                if (items && Object.keys(changedFields).length === 0) {
                  setAlarm({ type: 'error', message: 'No information was changed, nothing to update.' });
                  closeModal();
                  return;
                }
                
                const updateData = {
                  user_ids: selected.map((item) => item.id),
                  selectAll: checkedAllUsers && checkedAllUsers === true ? true : false,
                  searchQuery: searchQuery,
                  filter: filter,
                  update_data: changedFields,
                  table_type: type
                };

                setIsProcessing(true);

                const result = await adminAPI.bulkUpdateUsers(updateData);

                setIsProcessing(false);

                setAlarm({ 
                  type: 'success', 
                  message: result.message
                });
                
                setOpenSideActionBoard();
                if (type === 'Active Users') {
                  setCheckedActiveUsers([]);
                  setCheckedAllActiveUsers(false);
                }
                if (type === 'Unconfirmed Users') {
                  setUnconfirmedUsers([]);
                  setCheckedAllUnconfirmedUsers(false);
                }
                if (type === 'Pending Invites') {
                  setPendingInvites([]);
                  setCheckedAllPendingInvites(false);
                }
                queryClient.invalidateQueries(queryListId);
                queryClient.invalidateQueries('userDetails');
                setSelected([]);
                closeModal();
              } else {
                closeModal();
                setIsProcessing(true);
                await processIndividualOperation();
              }
            } catch (error) {
              setIsProcessing(false);

              console.error('Error in update:', error);
              setAlarm({ type: 'error', message: 'Error updating users. Please try again.' });
              closeModal();
            }
          }}
          onCancel={() => {
            closeModal();
          }}
        />
      ),
    });
  };

  const processIndividualOperation = async () => {
    
    let emailsToProcess = [];
    let totalEmailCount = 0;
    
    if (selected) {
      emailsToProcess = selected.map((item) => item.email);
      totalEmailCount = emailsToProcess.length;
    } else {
      if (bulkEmailCount > 0 && bulkEmailList.length > 0) {
        emailsToProcess = bulkEmailList;
        totalEmailCount = bulkEmailCount;
      } else {
        emailsToProcess = emailList;
        totalEmailCount = emailList.length;
      }
    }
    // if (!checkedAllUsers || items.length < bulkEmailLimit) {
        formData.emails = emailsToProcess;
    // }

    if (items && selected?.length > 0) {
      const changedFields = getChangedFields(formData, initialFormData);

      if (Object.keys(changedFields).length === 0) {
        setAlarm({ type: 'error', message: 'Nothing changed.' });
        setIsProcessing(false);
        return;
      }

      const updateData = {
        user_ids: selected.map((item) => item.id),
        selectAll: false,
        searchQuery: searchQuery,
        filter: filter,
        update_data: changedFields,
        table_type: type,
      };

      try {
        const data = await adminAPI.bulkUpdateUsers(updateData);
        if (data?.job_id) {
          setProcessingMessage(data.message);
          setAlarm({ type: 'success', message: data.message });
        } else {
          setContent({
            content: <FeedbackModal results={data?.results} skipEmail={data?.skip_email} />,
          });
        }
        setOpenSideActionBoard();
        if (type === 'Active Users') {
          setCheckedActiveUsers([]);
          setCheckedAllActiveUsers(false);
        }
        if (type === 'Unconfirmed Users') {
          setUnconfirmedUsers([]);
          setCheckedAllUnconfirmedUsers(false);
        }
        if (type === 'Pending Invites') {
          setPendingInvites([]);
          setCheckedAllPendingInvites(false);
        }
        queryClient.invalidateQueries(queryListId);
        queryClient.invalidateQueries('userDetails');
        setSelected([]);
      } catch (error) {
        setAlarm({ type: 'error', message: error.message });
      } finally {
        setIsProcessing(false);
      }
      return;
    } else {
      const serializedData = JSON.stringify(formData);
      mutate(serializedData);
    }
  };

  useEffect(() => {
    if (type === 'Active Users') {
      setCheckedActiveUsers(selected);
    }
    if (type === 'Unconfirmed Users') {
      setUnconfirmedUsers(selected);
    }
    if (type === 'Pending Invites') {
      setPendingInvites(selected);
    }
  }, [selected]);

  return (
    !isLoading &&
    data && (
      <form className="p-8 overflow-y-scroll h-full" onSubmit={handleSubmit}>
        <h2 className="mb-8 font-semibold">{title}</h2>
        {!items ? (
          <>
            <ApplicationSelect
              onChange={(newApplication) => setApplication(newApplication)}
            />
            <EmailInputList
              onChangeEmailList={handleEmailListChange}
              isProcessing={isProcessing}
              processingMessage={processingMessage}
            />
          </>
        ) : (
          checkedAllUsers && items.length !== totalUsers ? (
            <>
            <div>
              <p className="text-sm uppercase text-charcoal font-bold mt-4 mb-4">
                Selected Users
              </p>
              <p className="text-sm font-bold mb-4" style={{ color: '#ef4444' }}>
                Updates will be applied to all {totalUsers?.toLocaleString('en-US')} users.
              </p>
            </div>
            </>
          ) : (
          <>
            <p className="text-sm uppercase text-charcoal font-bold mt-4 mb-4">
              Selected Users
            </p>
            {selected && selected.length === 1 && selected[0].selectAllFiltered ? (
              <div className="p-4 bg-purple-50 border-2 border-purple-200 rounded-lg mb-4">
                <p className="text-sm font-semibold text-purple-700 mb-2">
                  All Filtered Users Selected
                </p>
                <p className="text-sm text-purple-600">
                  Updates will be applied to all users matching your current filters and search criteria.
                </p>
                {selected[0].searchQuery && (
                  <p className="text-xs text-purple-500 mt-1">
                    Search: "{selected[0].searchQuery}"
                  </p>
                )}
              </div>
            ) : (
              <SimplifiedTable
                data={items}
                checkedItems={selected}
                setCheckedItems={setSelected}
              />
            )}
          </>
        ))}

        <UserInviteForm
          formData={{ user_invite: formData }}
          courses={data.enabled_courses}
          handleChange={(name, value, parent) => {
            if (parent === 'user_invite') {
              handleChange(name, value);
            } else {
              handleChange(name, value, parent);
            }
          }}
          alexAccessTypes={[{ label: 'N/A', value: 'N/A' }, ...(data.alex_access_type_options || [])]}
          hubspotAccessTypes={data.hubspot_options || []}
          cohorts={cohorts}
          limitFields={!!items}
          toggleIncludedField={toggleIncludedField}
          includedFields={includedFields}
          application={application}
          isMultiEditUser={!!items}
        />

        <button
          className={`p-4 my-10 font-semibold rounded-lg ${
            isProcessing || mutationIsLoading
              ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
              : 'bg-purple text-white hover:bg-purple-700'
          }`}
          type="submit"
          data-testid="admin-invite-form-submit"
          disabled={isProcessing || mutationIsLoading}
        >
          {isProcessing 
            ? 'Processing...' 
            : mutationIsLoading 
              ? 'Sending...' 
              : items 
                ? 'Update Users' 
                : 'Send Invitations'
          }
        </button>
        {mutationIsLoading && <FormLoader />}
      </form>
    )
  );
};

export default IndependentUserInviteForm;
